import {
  faArrowLeft,
  faBolt,
  faBookOpen,
  faClock,
  faDollarSign,
  faFire,
  faImage,
  faLeaf,
  faSave,
  faUser,
  faUtensils,
  faWheatAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate, useParams } from "react-router-dom";
import { fetchRecipeDetails, updateRecipe } from "../components/api";
import { auth } from "../components/firebase";

function EditRecipe() {
  const { recipeId } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState({});
  const [user] = useAuthState(auth);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    fetchRecipeDetails(recipeId)
      .then((recipeData) => {
        setRecipe(recipeData || {});
      })
      .catch((error) => {
        console.error("Error fetching recipe details:", error);
      })
      .finally(() => setLoading(false));
  }, [recipeId, user]);

  const toArray = (value) => {
    if (Array.isArray(value)) return value;
    if (!value) return [];
    return value
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const multilineValue = (value) => {
    if (Array.isArray(value)) return value.join("\n");
    return value || "";
  };

  const handleUpdateRecipe = () => {
    const payload = {
      ...recipe,
      ingredients: toArray(recipe.ingredients),
      instructions: toArray(recipe.instructions),
    };

    updateRecipe(recipeId, payload)
      .then(() => {
        navigate(`/recipe/${recipeId}`);
      })
      .catch((error) => {
        console.error("Error updating recipe:", error);
      });
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    setRecipe((prevRecipe) => ({
      ...prevRecipe,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  if (loading) {
    return <div className="loading-container">Loading...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 transition"
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>
          <h1 className="text-xl font-semibold text-gray-900">Edit Recipe</h1>
        </div>
        <button
          onClick={handleUpdateRecipe}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500 text-white shadow hover:bg-orange-600 transition"
        >
          <FontAwesomeIcon icon={faSave} /> Save changes
        </button>
      </div>

      <div className="grid lg:grid-cols-[1.25fr_0.75fr] gap-6 items-start">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <FontAwesomeIcon icon={faUtensils} /> Title
              </label>
              <input
                type="text"
                name="title"
                value={recipe.title || ""}
                onChange={handleInputChange}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                placeholder="e.g. Spicy Chickpea Bowl"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Cuisine</label>
              <input
                type="text"
                name="cuisine"
                value={recipe.cuisine || ""}
                onChange={handleInputChange}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                placeholder="Optional (e.g. Italian)"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <FontAwesomeIcon icon={faBookOpen} /> Description
            </label>
            <textarea
              name="description"
              value={recipe.description || ""}
              onChange={handleInputChange}
              rows={3}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
              placeholder="Short teaser for this recipe"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Difficulty</label>
              <select
                name="difficulty"
                value={recipe.difficulty || ""}
                onChange={handleInputChange}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
              >
                <option value="">Select difficulty</option>
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <FontAwesomeIcon icon={faImage} /> Image URL
              </label>
              <input
                type="text"
                name="imageURL"
                value={recipe.imageURL || ""}
                onChange={handleInputChange}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                placeholder="Paste an image link"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <FontAwesomeIcon icon={faFire} /> Calories
              </label>
              <input
                type="number"
                name="calories"
                value={recipe.calories || ""}
                onChange={handleInputChange}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                placeholder="e.g. 420"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <FontAwesomeIcon icon={faClock} /> Time (min)
              </label>
              <input
                type="number"
                name="cookingMinutes"
                value={recipe.cookingMinutes || ""}
                onChange={handleInputChange}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                placeholder="e.g. 30"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <FontAwesomeIcon icon={faUser} /> Servings
              </label>
              <input
                type="number"
                name="portionSize"
                value={recipe.portionSize || ""}
                onChange={handleInputChange}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                placeholder="e.g. 4"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-gray-800 bg-emerald-50 border border-emerald-100 px-3 py-2 rounded-full">
              <input
                type="checkbox"
                name="vegan"
                checked={recipe.vegan || false}
                onChange={handleInputChange}
                className="accent-emerald-500"
              />
              <FontAwesomeIcon icon={faLeaf} /> Vegan
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-800 bg-blue-50 border border-blue-100 px-3 py-2 rounded-full">
              <input
                type="checkbox"
                name="glutenFree"
                checked={recipe.glutenFree || false}
                onChange={handleInputChange}
                className="accent-blue-500"
              />
              <FontAwesomeIcon icon={faWheatAlt} /> Gluten free
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-800 bg-indigo-50 border border-indigo-100 px-3 py-2 rounded-full">
              <input
                type="checkbox"
                name="highProtein"
                checked={recipe.highProtein || false}
                onChange={handleInputChange}
                className="accent-indigo-500"
              />
              <FontAwesomeIcon icon={faBolt} /> High-protein
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-800 bg-green-50 border border-green-100 px-3 py-2 rounded-full" title="Perfect for students: costs less than €15 per person and quick to make">
              <input
                type="checkbox"
                name="budgetFriendly"
                checked={recipe.budgetFriendly || false}
                onChange={handleInputChange}
                className="accent-green-500"
              />
              <FontAwesomeIcon icon={faDollarSign} /> Budget-Friendly
            </label>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <FontAwesomeIcon icon={faUtensils} /> Ingredients
              </label>
              <textarea
                name="ingredients"
                value={multilineValue(recipe.ingredients)}
                onChange={handleInputChange}
                rows={8}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                placeholder={"Each ingredient on a new line\n1 cup of flour"}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <FontAwesomeIcon icon={faBookOpen} /> Method steps
              </label>
              <textarea
                name="instructions"
                value={multilineValue(recipe.instructions)}
                onChange={handleInputChange}
                rows={8}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                placeholder={"Each step on a new line\n1. Preheat oven\n2. Mix flour and baking powder"}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleUpdateRecipe}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500 text-white shadow hover:bg-orange-600 transition"
            >
              <FontAwesomeIcon icon={faSave} /> Save changes
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4 lg:sticky lg:top-6">
          <div className="aspect-[4/3] w-full overflow-hidden rounded-2xl bg-gradient-to-br from-orange-50 via-white to-purple-50 border border-gray-100">
            {recipe.imageURL ? (
              <img
                src={recipe.imageURL}
                alt={recipe.title || "Recipe preview"}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                Image preview
              </div>
            )}
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">
              {recipe.title || "Untitled recipe"}
            </h2>
            <p className="text-sm text-gray-600">
              {recipe.description || "Add a short description to preview it here."}
            </p>
            <div className="flex flex-wrap gap-2">
              {recipe.cuisine && (
                <span className="px-3 py-1 rounded-full text-sm bg-purple-50 text-purple-700 border border-purple-100">
                  {recipe.cuisine}
                </span>
              )}
              {recipe.vegan && (
                <span className="px-3 py-1 rounded-full text-sm bg-emerald-50 text-emerald-700 border border-emerald-100">
                  Vegan
                </span>
              )}
              {recipe.glutenFree && (
                <span className="px-3 py-1 rounded-full text-sm bg-blue-50 text-blue-700 border border-blue-100">
                  Gluten free
                </span>
              )}
              {recipe.highProtein && (
                <span className="px-3 py-1 rounded-full text-sm bg-indigo-50 text-indigo-700 border border-indigo-100">
                  High protein
                </span>
              )}
              {recipe.budgetFriendly && (
                <span className="px-3 py-1 rounded-full text-sm bg-green-50 text-green-700 border border-green-100">
                  Budget-Friendly
                </span>
              )}
              {recipe.calories && (
                <span className="px-3 py-1 rounded-full text-sm bg-orange-50 text-orange-700 border border-orange-100">
                  {recipe.calories} kcal
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditRecipe;
